/**
 * Plotfy Universal Media & Image Utilities
 * --------------------------------------------------------
 * Handles cross-device image URL resolution (Desktop, iOS, Android)
 * and client-side smart screenshot optimization under 1 MB.
 */

/**
 * Resolves an uploaded media path to a full, valid URL across environments.
 * Handles /uploads, /api/uploads, absolute URLs, and data/blob URIs.
 * 
 * @param {string} path - Media path (e.g. '/uploads/2026/...', 'https://...', 'blob:...')
 * @returns {string} Fully resolved media URL
 */
export function getMediaUrl(path) {
  if (!path || typeof path !== 'string') return ''
  const trimmed = path.trim()
  if (!trimmed) return ''

  // Already absolute URL or blob/data URI
  if (/^(https?:|\/\/|data:|blob:)/i.test(trimmed)) {
    return trimmed
  }

  // Ensure path starts with leading slash
  const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`

  // On production (cPanel / Nginx where /api maps to Node.js),
  // ensure /uploads/ paths can be served via /api/uploads/ as primary route
  if (cleanPath.startsWith('/uploads/')) {
    // In production web build, accessing /api/uploads/... guarantees hitting Node.js backend
    return `/api${cleanPath}`
  }

  return cleanPath
}

/**
 * Fallback handler for <img> tags to try alternative URLs if first load fails.
 * 
 * @param {Event} event - Image error event
 * @param {string} originalSrc - The original source path
 */
export function handleImageError(event, originalSrc) {
  const img = event?.currentTarget
  if (!img) return

  const currentSrc = img.src || ''

  // If failed on /api/uploads/..., try direct /uploads/...
  if (currentSrc.includes('/api/uploads/')) {
    img.src = currentSrc.replace('/api/uploads/', '/uploads/')
    return
  }

  // If failed on /uploads/..., try /api/uploads/...
  if (currentSrc.includes('/uploads/') && !currentSrc.includes('/api/uploads/')) {
    img.src = currentSrc.replace('/uploads/', '/api/uploads/')
    return
  }

  // Final fallback to a placeholder SVG
  img.onerror = null
  img.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 24 24" fill="none" stroke="%2364748b" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>'
}

/**
 * Smart Client-side Image Optimizer (iOS / Android / Desktop)
 * Compresses and normalizes screenshots to stay well under 1 MB with crystal-clear quality.
 * 
 * @param {File} file - Original file from file input
 * @param {Object} options - Compression options
 * @returns {Promise<File>} Optimized File ready for upload
 */
export async function compressImageForUpload(file, options = {}) {
  if (!file || !(file instanceof File)) return file

  const maxSizeBytes = options.maxSizeBytes || 950 * 1024 // 950 KB target (safe under 1 MB limit)
  const maxWidth = options.maxWidth || 1920
  const maxHeight = options.maxHeight || 1920
  const preserveAlpha = Boolean(
    options.preserveAlpha ||
    ['image/png', 'image/webp', 'image/svg+xml'].includes(file.type)
  )

  // If file is already small (<= 500KB) and is a standard WebP or PNG/JPEG, return as is
  if (file.size <= 500 * 1024 && ['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    return file
  }

  return new Promise((resolve) => {
    // If not in browser environment (SSR / test), return original
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return resolve(file)
    }

    const reader = new FileReader()
    reader.onerror = () => resolve(file)
    reader.onload = (e) => {
      const img = new Image()
      img.onerror = () => resolve(file)
      img.onload = () => {
        try {
          let width = img.width
          let height = img.height

          // Calculate aspect ratio scaling
          if (width > maxWidth || height > maxHeight) {
            if (width / height > maxWidth / maxHeight) {
              height = Math.round((height * maxWidth) / width)
              width = maxWidth
            } else {
              width = Math.round((width * maxHeight) / height)
              height = maxHeight
            }
          }

          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext('2d')
          if (!ctx) return resolve(file)

          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'
          ctx.drawImage(img, 0, 0, width, height)

          // Preserve transparency with WebP (fallback to JPEG only for opaque photos without alpha)
          const outputType = preserveAlpha ? 'image/webp' : 'image/jpeg'
          const ext = preserveAlpha ? '.webp' : '.jpg'
          const quality = 0.85

          canvas.toBlob(
            (blob) => {
              if (!blob) return resolve(file)

              // If output blob is successfully created
              const fileName = file.name.replace(/\.[^/.]+$/, '') + ext
              const optimizedFile = new File([blob], fileName, {
                type: outputType,
                lastModified: Date.now(),
              })

              resolve(optimizedFile)
            },
            outputType,
            quality,
          )
        } catch {
          resolve(file)
        }
      }
      img.src = e.target?.result
    }
    reader.readAsDataURL(file)
  })
}

/**
 * Dynamically updates browser tab favicon and apple-touch-icon
 * @param {string} url - Favicon image URL or null for default
 */
export function updateFavicon(url) {
  if (typeof document === 'undefined') return
  const finalHref = url ? getMediaUrl(url) : '/images/plotfy-logo-icon.png'

  let iconLink = document.querySelector("link[rel~='icon']")
  if (!iconLink) {
    iconLink = document.createElement('link')
    iconLink.rel = 'icon'
    document.head.appendChild(iconLink)
  }
  iconLink.href = finalHref

  let appleLink = document.querySelector("link[rel='apple-touch-icon']")
  if (!appleLink) {
    appleLink = document.createElement('link')
    appleLink.rel = 'apple-touch-icon'
    document.head.appendChild(appleLink)
  }
  appleLink.href = finalHref
}

